/**
 * "Open Email" — the second half of landing from an ora alert.
 *
 * Lives in the step drawer's header (`PinnedHeader`'s `action`), with the
 * client and policy it is context for, and renders the shared `OpenEmailLink`
 * so it looks and reads the same as the Ready-to-start card's hop. It used to
 * be its own row, labelled "Open the original request", under the header on
 * most views and inside "Launched from" on the exception view: one control in
 * two places under a name no other surface used.
 *
 * Story 26738. Clicking an ora notification (or its card's Open in add-in
 * button) opens the pane on the waiting decision. The Servicer is then looking
 * at ora's alert — NOT at the lender email that started the run, which is
 * usually the thing they need to read before they can answer.
 *
 * This is the hop back to it. `displayMessageForm` opens the original in an
 * inspector; it is the same call the ready-briefs queue already uses, and the
 * run has carried its `graph_message_id` since launch.
 *
 * A BUTTON, NOT AUTOMATIC. Opening an inspector window nobody asked for is the
 * kind of thing people turn add-ins off over, and half the time the Servicer
 * only wants to approve and move on.
 *
 * IT REPORTS ITS OWN FAILURES. `displayMessageForm` is fire-and-forget and says
 * nothing when its id resolves to no message — a stale id, a message deleted
 * since launch — so a click could otherwise produce no window, no error, and no
 * way to tell a broken feature from a bad link. `openMessageInOutlook` returns a
 * result and this renders it where the click happened, exactly as
 * `NeedsYouTab` does.
 */

import { useState } from "react";

import { OpenEmailLink } from "@product-app/common/workflows/components/OpenEmailLink";

import { openMessageInOutlook } from "../outlook/openMessage";

export interface SourceRequestRowProps {
  /** The run's own `graph_message_id` — the email it was launched from. */
  sourceMessageId?: string | null;
  /**
   * The message the pane is currently showing, when known. Used only to hide
   * the row when the Servicer is already reading the original.
   *
   * BEST EFFORT: the pane's id and the run's stored id are not guaranteed to be
   * in the same format (EWS vs REST), so a mismatch can leave the row showing on
   * the very message it points at. That is a cosmetic miss, not a failure — the
   * hop still opens the right message — and it is the safe direction to err in,
   * because the alternative is hiding the button on ora's alert where it is the
   * whole point.
   */
  currentMessageId?: string | null;
  className?: string;
}

export function SourceRequestRow({
  sourceMessageId,
  currentMessageId,
  className,
}: SourceRequestRowProps) {
  const [error, setError] = useState<string | null>(null);

  const id = (sourceMessageId ?? "").trim();
  if (!id) return null;
  if (currentMessageId && currentMessageId.trim() === id) return null;

  return (
    <div
      className={`flex flex-col items-end ${className ?? ""}`.trim()}
      data-testid="source-request-row"
    >
      <OpenEmailLink
        testId="open-source-request"
        onClick={() => {
          const result = openMessageInOutlook(id);
          // Cleared on success so a later good click removes a stale notice.
          setError(result.ok ? null : result.message);
        }}
      />
      {error ? (
        <div
          role="alert"
          data-testid="open-source-request-error"
          className="mt-1 max-w-[200px] rounded-md border border-primary-warning-300 bg-primary-warning-50 px-2 py-1 text-right text-2xs text-primary-warning-700"
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}

export default SourceRequestRow;
